import { useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlineOutlined';
import { matchPath, useInRouterContext, useLocation } from 'react-router';
import { hasCapability, useSession } from '../session/session';
import { PAGE_HELP, type PageHelpEntry } from './help-content';

const HELP_TITLE_ID = 'page-help-title';

/**
 * The help entry for a path: an exact static route wins over a parameterised one, so
 * `/procurement/purchase-orders/new` is not read as the order with id "new".
 */
export function helpFor(pathname: string): PageHelpEntry | null {
  let best: { entry: PageHelpEntry; params: number } | null = null;
  for (const entry of PAGE_HELP) {
    if (matchPath({ path: entry.path, end: true }, pathname) === null) continue;
    const params = entry.path.split('/').filter((s) => s.startsWith(':')).length;
    if (best === null || params < best.params) best = { entry, params };
  }
  return best?.entry ?? null;
}

/**
 * The page header's help button (FRD-CORE-041). Renders nothing where a page has no entry, or
 * outside a router (a component rendered on its own in a test).
 */
export function PageHelp() {
  return useInRouterContext() ? <RoutedPageHelp /> : null;
}

function RoutedPageHelp() {
  const location = useLocation();
  const entry = helpFor(location.pathname);
  const [open, setOpen] = useState(false);
  if (entry === null) return null;
  return (
    <>
      <Tooltip title="Help for this page">
        <IconButton
          aria-label="Help for this page"
          onClick={() => {
            setOpen(true);
          }}
          size="small"
          sx={{ flexShrink: 0 }}
        >
          <HelpOutlineIcon />
        </IconButton>
      </Tooltip>
      <Drawer
        anchor="right"
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        slotProps={{
          paper: {
            role: 'dialog',
            'aria-labelledby': HELP_TITLE_ID,
            sx: { width: { xs: '100%', sm: 420 } },
          },
        }}
      >
        <HelpPanel
          entry={entry}
          onClose={() => {
            setOpen(false);
          }}
        />
      </Drawer>
    </>
  );
}

function HelpPanel({ entry, onClose }: { entry: PageHelpEntry; onClose: () => void }) {
  const session = useSession();
  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography id={HELP_TITLE_ID} variant="h6" component="h2">
          Help: {entry.title}
        </Typography>
        <IconButton aria-label="Close help" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Stack>

      <Typography variant="subtitle2" component="h3">
        What this page does
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5, mb: 2 }}>
        {entry.summary}
      </Typography>

      {entry.tasks !== undefined && entry.tasks.length > 0 && (
        <>
          <Typography variant="subtitle2" component="h3">
            What you can do here
          </Typography>
          <List dense disablePadding sx={{ mb: 2, listStyleType: 'disc', pl: 2.5 }}>
            {entry.tasks.map((task) => (
              <ListItem key={task} disableGutters sx={{ display: 'list-item', py: 0 }}>
                <ListItemText primary={task} slotProps={{ primary: { variant: 'body2' } }} />
              </ListItem>
            ))}
          </List>
        </>
      )}

      <Divider sx={{ mb: 2 }} />
      <Typography variant="subtitle2" component="h3">
        Permissions it uses
      </Typography>
      <List dense disablePadding sx={{ mb: 2 }}>
        {entry.permissions.map((permission) => {
          // A pattern such as `<record type>:view` names a family, not one permission.
          const concrete = !permission.capability.includes('<');
          const held = concrete && hasCapability(session.data, permission.capability);
          return (
            <ListItem key={permission.capability} disableGutters sx={{ alignItems: 'flex-start' }}>
              <ListItemText
                primary={
                  <Box component="code" sx={{ fontSize: '0.8125rem', wordBreak: 'break-all' }}>
                    {permission.capability}
                  </Box>
                }
                secondary={permission.purpose}
              />
              {concrete && (
                <Chip
                  size="small"
                  variant="outlined"
                  color={held ? 'success' : 'default'}
                  label={held ? 'You have this' : 'Not held'}
                  sx={{ ml: 1, mt: 0.5, flexShrink: 0 }}
                />
              )}
            </ListItem>
          );
        })}
      </List>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Permissions come only from the roles assigned to you (Administration › Roles &amp;
        permissions); the system never checks a role’s name.
      </Typography>

      {entry.configuration !== undefined && entry.configuration.length > 0 && (
        <>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" component="h3">
            Configuration it needs
          </Typography>
          <List dense disablePadding sx={{ listStyleType: 'disc', pl: 2.5 }}>
            {entry.configuration.map((item) => (
              <ListItem key={item} disableGutters sx={{ display: 'list-item', py: 0 }}>
                <ListItemText primary={item} slotProps={{ primary: { variant: 'body2' } }} />
              </ListItem>
            ))}
          </List>
        </>
      )}
    </Box>
  );
}
